/**
 * Zod Schemas for AI Suggestions Writeback (SPEC-014)
 *
 * Provides runtime validation schemas for AI Button writeback functionality:
 * - AiLink: Single AI-generated link
 * - AiWriteback: Frontmatter writeback structure
 * - AIApplyRequest: Apply request body
 * - AIApplyResult: Apply response body
 *
 * @module schemas/ai-suggestions.zod
 * @see SPEC-014 Requirements 1.1, 1.2, 1.3, 1.4, 1.6
 */
import { z } from 'zod';
// ============================================================================
// Constants
// ============================================================================
/**
 * Maximum number of AI tags in frontmatter.
 * @see Requirement 1.2 - ai_tags max 8
 */
export const AI_TAGS_MAX = 8;
/**
 * Maximum number of AI links in frontmatter.
 * @see Requirement 1.2 - ai_links max 8
 */
export const AI_LINKS_MAX = 8;
/**
 * Maximum length for recommendation reason text.
 * @see Requirement 1.1 - reason ≤ 140 chars
 */
export const MAX_REASON_LENGTH = 140;
// ============================================================================
// AiLink Schema
// ============================================================================
/**
 * Schema for a single AI-generated link.
 *
 * @see Requirement 1.1
 */
export const AiLinkSchema = z.object({
    /** Target document ID */
    id: z.string().min(1, 'id is required'),
    /** Snapshot title (optional) */
    title: z.string().optional(),
    /** Explanation for the link (≤140 chars) */
    reason: z.string().max(MAX_REASON_LENGTH, `reason must be ≤${MAX_REASON_LENGTH} chars`),
    /** Debug/analysis score (optional) */
    score: z.number().optional(),
    /** Source identifier - always "ai" for AI-generated links */
    source: z.literal('ai'),
    /** Timestamp when the link was applied (ms since epoch) */
    appliedAtMs: z.number().nonnegative(),
});
// ============================================================================
// AiWriteback Schema
// ============================================================================
/**
 * Schema for frontmatter writeback structure.
 *
 * @see Requirement 1.2
 */
export const AiWritebackSchema = z.object({
    /** AI-generated tags (max 8) */
    ai_tags: z.array(z.string()).max(AI_TAGS_MAX, `ai_tags must have ≤${AI_TAGS_MAX} items`),
    /** AI-generated links (max 8) */
    ai_links: z.array(AiLinkSchema).max(AI_LINKS_MAX, `ai_links must have ≤${AI_LINKS_MAX} items`),
});
// ============================================================================
// AIApplyRequest Schema
// ============================================================================
/**
 * Schema for apply request selection.
 */
const ApplySelectionSchema = z.object({
    /** Selected tags */
    tags: z.array(z.string()),
    /** Selected backlink docIds */
    backlinks: z.array(z.string()),
});
/**
 * Schema for POST /ai/apply request body.
 *
 * @see Requirement 1.3
 */
export const AIApplyRequestSchema = z.object({
    /** Document ID to apply suggestions to */
    docId: z.string().min(1, 'docId is required'),
    /** Proposal ID from suggest response */
    proposalId: z.string().min(1, 'proposalId is required'),
    /** Candidates accepted by user */
    accepted: ApplySelectionSchema,
    /** Candidates rejected by user */
    rejected: ApplySelectionSchema,
});
// ============================================================================
// AIApplyResult Schema
// ============================================================================
/**
 * Schema for POST /ai/apply response body.
 *
 * @see Requirement 1.4
 */
export const AIApplyResultSchema = z.object({
    /** Whether apply was successful */
    success: z.boolean(),
    /** Tags that were applied */
    appliedTags: z.array(z.string()),
    /** Link docIds that were applied */
    appliedLinks: z.array(z.string()),
    /** Optional warnings (e.g., truncation) */
    warnings: z.array(z.string()).optional(),
});
//# sourceMappingURL=ai-suggestions.zod.js.map